import {
  estadoAccion,
  etapaActiva,
  frescuraDe,
  diasHastaFin,
  proximaAccion,
  type Etapa,
  type Fase,
  type Frescura,
  type Obra,
  type TipoProyecto,
} from './modelo.js';
import { puntuar } from './puntuacion.js';
import { sinTildes } from './texto.js';

/**
 * Filtros, busqueda y orden.
 *
 * VIVEN AQUI Y NO EN LA PANTALLA porque el mapa, la lista y los contadores
 * tienen que ensenar exactamente lo mismo. Con la logica repetida en tres
 * sitios, el dia que alguien toca uno los otros dos empiezan a mentir, y un
 * contador que no cuadra con la lista que hay debajo destruye la confianza en
 * todo lo demas.
 */

export interface Filtros {
  texto: string;
  municipios: string[];
  zonas: string[];
  fases: Fase[];
  etapas: Etapa[];
  tipos: TipoProyecto[];
  responsables: string[];
  progresoMin: number;
  progresoMax: number;
  /** Solo obras que terminan dentro de N dias. `null` = sin limite. */
  finEnDias: number | null;
  puntuacionMin: number;
  /** Estados de la proxima accion que se aceptan. Vacio = todos. */
  estadosAccion: Array<'VENCIDA' | 'HOY' | 'PROXIMA' | 'SIN_ACCION'>;
  frescuras: Frescura[];
  valorMinCents: number | null;
  /** `null` = da igual; `true` = solo con propietario conocido. */
  conPropietario: boolean | null;
  enVenta: boolean | null;
  /** Sin esto, las ganadas y perdidas ensucian el trabajo del dia. */
  soloActivas: boolean;
}

export const FILTROS_VACIOS: Filtros = {
  texto: '',
  municipios: [],
  zonas: [],
  fases: [],
  etapas: [],
  tipos: [],
  responsables: [],
  progresoMin: 0,
  progresoMax: 100,
  finEnDias: null,
  puntuacionMin: 0,
  estadosAccion: [],
  frescuras: [],
  valorMinCents: null,
  conPropietario: null,
  enVenta: null,
  soloActivas: false,
};

export function hayFiltros(f: Filtros): boolean {
  return JSON.stringify(f) !== JSON.stringify(FILTROS_VACIOS);
}

/**
 * Todo el texto por el que se puede buscar una obra, junto y SIN TILDES.
 *
 * Nadie escribe «Sóller» con tilde en un buscador, y una busqueda que no
 * encuentra lo que sabe que esta ahi se abandona a la segunda vez. El termino
 * buscado pasa por la misma funcion, para que los dos lados coincidan.
 */
function textoBuscable(obra: Obra): string {
  return sinTildes(
    [
      obra.nombre,
      obra.codigo,
      obra.direccion,
      obra.municipio,
      obra.zona,
      obra.responsable ?? '',
      obra.etiquetas.join(' '),
      obra.contactos.map((c) => c.nombre).join(' '),
    ].join(' '),
  );
}

export function aplicar(obras: Obra[], f: Filtros, hoy: string): Obra[] {
  const busca = sinTildes(f.texto.trim());

  return obras.filter((obra) => {
    if (busca !== '' && !textoBuscable(obra).includes(busca)) return false;
    if (f.soloActivas && !etapaActiva(obra.etapa)) return false;
    if (f.municipios.length > 0 && !f.municipios.includes(obra.municipio)) return false;
    if (f.zonas.length > 0 && !f.zonas.includes(obra.zona)) return false;
    if (f.fases.length > 0 && !f.fases.includes(obra.fase)) return false;
    if (f.etapas.length > 0 && !f.etapas.includes(obra.etapa)) return false;
    if (f.tipos.length > 0 && !f.tipos.includes(obra.tipoProyecto)) return false;
    if (f.responsables.length > 0 && !f.responsables.includes(obra.responsable ?? '')) return false;
    if (obra.progreso < f.progresoMin || obra.progreso > f.progresoMax) return false;

    if (f.finEnDias !== null) {
      const dias = diasHastaFin(obra, hoy);
      // Sin fecha no se puede afirmar que termine pronto, asi que no entra.
      if (dias === null || dias > f.finEnDias) return false;
    }

    if (f.puntuacionMin > 0 && puntuar(obra, hoy).total < f.puntuacionMin) return false;

    if (f.estadosAccion.length > 0 && !f.estadosAccion.includes(estadoAccion(obra, hoy))) {
      return false;
    }

    if (f.frescuras.length > 0 && !f.frescuras.includes(frescuraDe(obra, hoy))) return false;

    if (f.valorMinCents !== null) {
      if (obra.valorMercadoCents === null || obra.valorMercadoCents < f.valorMinCents) return false;
    }

    if (f.conPropietario !== null) {
      if ((obra.situacion.propietarioConocido === true) !== f.conPropietario) return false;
    }

    if (f.enVenta !== null) {
      const enVenta = obra.situacion.yaEnVenta === true || obra.situacion.saldraAVenta === true;
      if (enVenta !== f.enVenta) return false;
    }

    return true;
  });
}

// --- Orden ------------------------------------------------------------------

export const ORDENES = [
  'PUNTUACION',
  'PROGRESO',
  'FINALIZACION',
  'VALOR',
  'COMPROBACION',
  'ACCION',
] as const;
export type Orden = (typeof ORDENES)[number];

/** Lo que no tiene dato va SIEMPRE al final, se ordene como se ordene. */
function conNulosAlFinal(a: number | null, b: number | null, descendente: boolean): number {
  if (a === null && b === null) return 0;
  if (a === null) return 1;
  if (b === null) return -1;
  return descendente ? b - a : a - b;
}

export function ordenar(obras: Obra[], orden: Orden, hoy: string): Obra[] {
  const copia = obras.slice();
  switch (orden) {
    case 'PUNTUACION':
      return copia.sort((a, b) => puntuar(b, hoy).total - puntuar(a, hoy).total);
    case 'PROGRESO':
      return copia.sort((a, b) => b.progreso - a.progreso);
    case 'FINALIZACION':
      // La que antes termina, primero: es la que antes hay que atender.
      return copia.sort((a, b) =>
        conNulosAlFinal(diasHastaFin(a, hoy), diasHastaFin(b, hoy), false),
      );
    case 'VALOR':
      return copia.sort((a, b) => conNulosAlFinal(a.valorMercadoCents, b.valorMercadoCents, true));
    case 'COMPROBACION':
      // Lo mas viejo primero: es lo que hay que ir a verificar.
      return copia.sort((a, b) => {
        const fa = a.ultimaComprobacion;
        const fb = b.ultimaComprobacion;
        if (fa === null && fb === null) return 0;
        if (fa === null) return -1;
        if (fb === null) return 1;
        return fa.localeCompare(fb);
      });
    case 'ACCION':
      return copia.sort((a, b) => {
        const aa = proximaAccion(a);
        const ab = proximaAccion(b);
        if (!aa && !ab) return 0;
        if (!aa) return 1;
        if (!ab) return -1;
        return aa.cuando.localeCompare(ab.cuando);
      });
  }
}

// --- Contadores de cabecera -------------------------------------------------

export interface Contadores {
  activas: number;
  pendientesContactar: number;
  enContacto: number;
  finProxima: number;
  vencidas: number;
  prioridadAlta: number;
}

/** Cuantos dias cuentan como «termina pronto». Un solo sitio. */
export const DIAS_FIN_PROXIMA = 90;

export function contar(obras: Obra[], hoy: string): Contadores {
  let activas = 0;
  let pendientesContactar = 0;
  let enContacto = 0;
  let finProxima = 0;
  let vencidas = 0;
  let prioridadAlta = 0;

  for (const obra of obras) {
    const viva = etapaActiva(obra.etapa);
    if (viva) activas += 1;

    if (viva && (obra.etapa === 'DETECTADA' || obra.etapa === 'INVESTIGANDO' || obra.etapa === 'PENDIENTE_CONTACTAR')) {
      pendientesContactar += 1;
    }
    if (obra.etapa === 'CONTACTADA' || obra.etapa === 'CONVERSACION' || obra.etapa === 'REUNION' || obra.etapa === 'PROPUESTA') {
      enContacto += 1;
    }

    const dias = diasHastaFin(obra, hoy);
    if (viva && dias !== null && dias <= DIAS_FIN_PROXIMA) finProxima += 1;
    if (viva && estadoAccion(obra, hoy) === 'VENCIDA') vencidas += 1;
    if (puntuar(obra, hoy).prioridad === 'ALTA') prioridadAlta += 1;
  }

  return { activas, pendientesContactar, enContacto, finProxima, vencidas, prioridadAlta };
}
